import React from "react"
import {
	Grid,
	TextField,
	FormControl,
	Select,
	MenuItem
} from "@material-ui/core"
import { Autocomplete } from "@material-ui/lab"
import useDidMount from "@/hooks/useDidMount"
import TagService from "@/services/Tag"
import { Tag } from "@/protocols/tag"
import DateFilterOptions from "@/pages/Admin/ClientCatalog/ClientManageFilters/ContactsFilters/components/DateFilterOptions"
import {
	ConditionOperator,
	ContactsFiltersConditions,
	DateFilterState,
	ExtraFilterActions,
	FilterType
} from "@/protocols/ClientCatalogFilterProtocol"

type ContactTagSlugs =
	| "contact_contains_tag"
	| "contact_not_contains_tag"
	| "contact_has_tag_associated"
	| "contact_not_has_tag_associated";

type ContactTagOption = {
	operator: ConditionOperator;
	label: string;
	extraFilterAction?: ExtraFilterActions;
};

const contactTagSlugsToContactTagOptions: Record<
ContactTagSlugs,
ContactTagOption
> = {
	contact_contains_tag: {
		operator: "in",
		label: "Contato contém a tag"
	},
	contact_not_contains_tag: {
		operator: "not_in",
		label: "Contato não contém a tag"
	},
	contact_has_tag_associated: {
		operator: "in",
		label: "Contato foi associado à tag",
		extraFilterAction: "associated"
	},
	contact_not_has_tag_associated: {
		operator: "in",
		label: "Contato foi desassociado da tag",
		extraFilterAction: "disassociated"
	}
}

type ContactTagFilterConditionProps = {
	condition: ContactsFiltersConditions<"contact_tag">;
	onChange: (condition: ContactsFiltersConditions<FilterType>) => void;
};

const ContactTagFilterCondition: React.FC<ContactTagFilterConditionProps> = ({
	condition,
	onChange
}) => {
	const [tags, setTags] = React.useState<Tag[]>([])

	const getSlugFromCondition = (): ContactTagSlugs => {
		const action = condition?.extraFilter?.filterData?.action

		if (action === "associated") {
			return "contact_has_tag_associated"
		}
		if (action === "disassociated") {
			return "contact_not_has_tag_associated"
		}
		if (condition?.operator === "not_in") {
			return "contact_not_contains_tag"
		} else {
			return "contact_contains_tag"
		}
	}

	const slug = getSlugFromCondition()
	const extraFilterAction =
		contactTagSlugsToContactTagOptions[slug].extraFilterAction

	const handleChange = (
		slug: ContactTagSlugs,
		tagId: string,
		dateFilter?: DateFilterState
	) => {
		const { operator, extraFilterAction } =
			contactTagSlugsToContactTagOptions[slug]

		onChange({
			type: "contact_tag",
			operator,
			filterData: { tag_id: tagId },
			extraFilter: extraFilterAction
				? {
					operator: dateFilter?.operator || "between",
					filterData: {
						action: extraFilterAction,
						dateFrom: dateFilter?.dateFrom,
						dateTo: dateFilter?.dateTo
					}
				}
				: undefined
		})
	}

	useDidMount(async () => {
		const data = await TagService.getTags()
		if (data?.length) {
			setTags(data)
		}
	})

	return (
		<Grid container direction="row" spacing={2}>
			<Grid item xs={extraFilterAction ? 3 : 6}>
				<FormControl fullWidth variant="outlined" size="small">
					<Select
						variant="outlined"
						value={slug}
						onChange={({ target }) =>
							handleChange(
								target.value as ContactTagSlugs,
								condition.filterData?.tag_id ?? "",
								{
									operator: condition.extraFilter?.operator || "between",
									dateFrom: condition.extraFilter?.filterData?.dateFrom,
									dateTo: condition.extraFilter?.filterData?.dateTo
								}
							)
						}
						fullWidth
					>
						{Object.entries(contactTagSlugsToContactTagOptions).map(([key, option]) => (
							<MenuItem key={key} value={key}>
								{option.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Grid>

			<Grid item xs={extraFilterAction ? 3 : 6}>
				<Autocomplete
					id="tags-outlined"
					options={tags}
					value={tags.find((tag) => String(tag.id) === String(condition.filterData?.tag_id)) || null
					}
					getOptionLabel={(opt) => opt.name}
					limitTags={1}
					fullWidth
					onChange={(_, tag) => {
						handleChange(
							slug,
							tag ? String(tag.id) : "",
							{
								operator: condition.extraFilter?.operator || "between",
								dateFrom: condition.extraFilter?.filterData?.dateFrom,
								dateTo: condition.extraFilter?.filterData?.dateTo
							})
					}}
					renderInput={(params) => (
						<TextField
							{...params}
							variant="outlined"
							placeholder="Tags"
						/>
					)}
					size="small"
				/>
			</Grid>

			{extraFilterAction && (
				<Grid item xs={6}>
					<DateFilterOptions
						onlyBetween
						operator={condition.extraFilter?.operator || "between"}
						dateFrom={condition.extraFilter?.filterData?.dateFrom}
						dateTo={condition.extraFilter?.filterData?.dateTo}
						onChange={(next) => handleChange(slug, condition.filterData?.tag_id, next)}
					/>
				</Grid>
			)}
		</Grid>
	)
}

export default ContactTagFilterCondition
